import React, { useEffect } from 'react'
import MicroModal from "micromodal"

import {
  ColumnDef,
  SortingState
} from '@tanstack/react-table'

import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { UserReport, UserReportData } from './data/UserReportData'
import UserCourseList from '../../Table/Modal/UserCourseList'
import { useUserReport } from '../context/UserReportContext'
import CourseUserDetails from '../../Table/Modal/CourseUserDetails'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { getExportHeaders } from '../types/tableHeaders'
import UserQuiz from '../../Table/Modal/UserQuiz'
import Button from '../../../../common/button/Button'

const UserReport = ({props, teamData, siteData }) => {
  const { setSelectedUser, setGroupReportSnapshot, datePicker, setTeamData, setSiteOption } = useUserReport()
  props.datePicker = datePicker
  
  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [data, setData] = React.useState(() => UserReportData(props, teamData ))

  useEffect(()=>{
    setGroupReportSnapshot(props)
    setTeamData( teamData )
    setSiteOption( siteData )
  },[])

  useEffect(()=>{
    setData( () => UserReportData( props, teamData ))
  }, [ datePicker ])

  MicroModal.init()

  const columns = React.useMemo<ColumnDef<UserReport>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'userId',
            accessorKey: 'userId',
            prettyName: 'User ID',
            show: false,
            cell: info => info.getValue(),
            header: () => <span>USER ID</span>,
          },
          {
            id: 'name',
            accessorKey: 'name',
            prettyName: 'Name',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>NAME</span>,
          },
          {
            id: 'email',
            accessorKey: 'email',
            prettyName: 'Email',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>EMAIL</span>,
          },
          {
            id: 'latestActivity',
            accessorKey: 'latestActivity',
            prettyName: 'Latest Activity',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>LATEST ACTIVITY</span>,
          },
          { 
            id: 'enrolledCount',
            accessorKey: 'enrolledCount',
            prettyName: 'Enrolled',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>ENROLLED COURSES</span>,
          },
          { 
            id: 'notStartedCount',
            accessorKey: 'notStartedCount',
            prettyName: 'Not Started',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>NOT STARTED</span>,
          },
          { 
            id: 'inProgressCount',
            accessorKey: 'inProgressCount',
            prettyName: 'In Progress',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>IN PROGRESS</span>,
          },
          { 
            id: 'completedCount',
            accessorKey: 'completedCount',
            prettyName: 'Completed',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>COMPLETED</span>,
          },
          { 
            id: 'viewDetails',
            accessorKey: 'viewDetails',
            prettyName: 'Action',
            cell: (info) => (
              <ViewDetails info={info.row.original} />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
          },
        ],
      },
    ],
    []
  )

  const exportHeaders = getExportHeaders('user-report')

  const handleOpenModal = (info) => {
    setSelectedUser( info )
    MicroModal.show(`msync-report-user-course-list`)
  }

  const ViewDetails = ({info}) => {
    return (
      <Button 
        name='View Details' 
        className='msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline' 
        onClick={() => handleOpenModal(info)}
      />
    )
  }

  return (
    <TanstackContent
      data={data}
      columns={columns}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={sorting}
      setSorting={setSorting}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      exportHeaders={exportHeaders}
      from={'User Report'}
      context={'Users'}
      columnFilters={ null }
      setColumnFilters={ null }
    >
      <UserCourseList courses={props.courses} />
      <UserQuiz from={'user-report'} />
      <CourseUserDetails from={'user-report'} />
    </TanstackContent>
  )
}

export default UserReport

